// Expiry tracking (F-CTRL-N73) — the client half.
//
// The server decides what is expiring (days_until_expiry / earliest_expiry_days
// are computed against the business date there). This file only turns those
// numbers into words and chips, and works out the date to prefill when stock
// of a tracked item is being received.
import { BUSINESS_TIMEZONE } from '@/lib/utils'

export type ExpiryState = 'expired' | 'today' | 'soon' | 'ok' | 'none'

export const DEFAULT_EXPIRY_ALERT_DAYS = 7

/** What a receipt form needs to know about the item it is receiving. */
export interface ExpiryTracked {
  tracks_expiry?: boolean
  shelf_life_days?: number | null
}

const businessDateParts = new Intl.DateTimeFormat('en-CA', {
  timeZone: BUSINESS_TIMEZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

/**
 * Today's business date as YYYY-MM-DD: the calendar day in the business
 * timezone, rolled back across the day boundary (at 2 AM with a 5 AM boundary
 * it is still yesterday). Mirrors util.BusinessDate on the backend.
 */
export function businessTodayIso(boundaryHour = 5, now: Date = new Date()): string {
  const shifted = new Date(now.getTime() - boundaryHour * 60 * 60 * 1000)
  return businessDateParts.format(shifted)
}

/** iso + days, as YYYY-MM-DD. '' when iso is not a date. */
export function addDaysIso(iso: string, days: number): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim())
  if (!m) return ''
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + days))
  if (Number.isNaN(d.getTime())) return ''
  return d.toISOString().slice(0, 10)
}

/** The expiry to prefill for a receipt today: today + shelf life, or ''. */
export function defaultExpiryFor(item: ExpiryTracked | null | undefined, today: string): string {
  const days = item?.shelf_life_days ?? 0
  if (!item?.tracks_expiry || !(days > 0)) return ''
  return addDaysIso(today, days)
}

/** True when a receipt of this item cannot be saved yet: tracked, no date. */
export function expiryMissing(item: ExpiryTracked | null | undefined, expiry: string): boolean {
  return Boolean(item?.tracks_expiry) && expiry.trim() === ''
}

export function expiryFieldLabel(item: ExpiryTracked | null | undefined): string {
  return item?.tracks_expiry ? 'Expiry date' : 'Expiry (optional)'
}

export function expiryState(daysUntil: number | null | undefined, windowDays: number = DEFAULT_EXPIRY_ALERT_DAYS): ExpiryState {
  if (daysUntil == null || !Number.isFinite(daysUntil)) return 'none'
  if (daysUntil < 0) return 'expired'
  if (daysUntil === 0) return 'today'
  return daysUntil <= windowDays ? 'soon' : 'ok'
}

export function expiryLabel(daysUntil: number | null | undefined): string {
  if (daysUntil == null || !Number.isFinite(daysUntil)) return ''
  if (daysUntil < 0) return `Expired ${Math.abs(daysUntil)}d ago`
  if (daysUntil === 0) return 'Expires today'
  return `${daysUntil}d left`
}

export interface ExpiryTone {
  /** Badge variant (components/ui/badge). */
  badge: 'danger' | 'warning-surface' | 'neutral-surface'
  /** Chip classes, matching the Items table status chips. */
  chipCls: string
  /** Card tint for the Alerts tab. */
  cardCls: string
}

export function expiryTone(state: ExpiryState): ExpiryTone {
  switch (state) {
    case 'expired':
    case 'today':
      return {
        badge: 'danger',
        chipCls: 'bg-danger-surface text-danger-fg border border-danger-border',
        cardCls: 'border-danger-border bg-danger-surface/40',
      }
    case 'soon':
      return {
        badge: 'warning-surface',
        chipCls: 'bg-warning-surface text-warning-fg border border-warning-border',
        cardCls: 'border-warning-border bg-warning-surface/30',
      }
    default:
      return { badge: 'neutral-surface', chipCls: 'text-muted-foreground', cardCls: '' }
  }
}

/** Parses the shelf-life field: '' → undefined, otherwise whole days 1–3650. */
export function parseShelfLife(raw: string): { days?: number; error?: string } {
  const t = raw.trim()
  if (t === '') return {}
  const n = Number(t)
  if (!Number.isInteger(n) || n < 1 || n > 3650) return { error: 'Shelf life is a whole number of days, 1 to 3650' }
  return { days: n }
}
